<template>
  <div
    :class="[
      'tw-bg-white dark:tw-bg-black apDesktop:tw-bg-ap-snow apDesktop:dark:tw-bg-ap-nero',
      'about-us-section tw-relative apDesktop:tw-pb-[283px] tw-pb-[120px]',
      'apDesktop:tw-mt-[-300px] tw-mt-0',
    ]"
  >
    <section
      id="our-services"
      :class="[
        'tw-relative tw-flex tw-justify-center tw-transition-opacity tw-duration-500 tw-ease ',
        'tw-z-[10] tw-bg-transparent',
      ]"
    >
      <!-- Top Rectangle at the top of the section -->
      <div class="services-card tw-max-w-[1280px] apDesktop:tw-mx-[80px] tw-mx-6 desktop:tw-mx-[80px] tw-flex tw-flex-col">
        <img
          :src="topRectangle"
          alt="Top Rectangle"
          class="tw-w-[120px] tw-h-[160px] tw-flex-shrink-0 tw-translate-y-[-50px] apDesktop:tw-block tw-hidden"
        >
        <!-- Title and Description Section -->
        <div class="">
          <h1
            :class="[
              'tw-pt-8',
              'apDesktop:tw-text-h4-bold-desktop-800 tw-text-h4-bold-mobile-800 tw-uppercase tw-max-w-[645px]  dark:tw-text-white tw-text-ap-nero '
            ]"
          >
            Our Services
          </h1>
        </div>

        <!-- Card Grid Section -->
        <CardGrid :cards="cardDatas" />
        <img
          id="orange-hexagon"
          :src="orangeHexagon"
          alt="Orange Hexagon"
          class="tw-w-[320px] tw-h-[320px] tw-flex-shrink-0 tw-absolute tw-bottom-[-230px] tw-right-[500px] apDesktop:tw-block tw-hidden"
        >
      </div>
    </section>
    <img
      id="moving-circle-image"
      :src="movingCircle"
      alt="Moving Circle"
      class="tw-w-[800px] tw-h-[800px] tw-absolute tw-bottom-[-120px] tw-right-[-100px] tw-z-[8] tw-object-contain apDesktop:tw-block tw-hidden"
    >
    <div
      id="what-we-do-background-card"
      :class="[
        'tw-absolute tw-top-0 tw-left-0 tw-bg-ap-white dark:tw-bg-black tw-min-h-[630px] tw-w-[1280px] tw-z-[9]'
      ]"
      :style="{ left: padLeft + 'px' }"
    />
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { cdn } from '../../support/cdn';
import { watchDarkMode } from '../../support/helper'; // Import watchDarkMode from helper
import CardGrid from './card-grid.vue';
gsap.registerPlugin(ScrollTrigger);

interface TempCard {
  iconLight: string;
  iconDark: string;
  text: string;
  tagline: string;
  description: string;
  slug: string;
}

// Icons for light and dark mode

const props = defineProps({
  cardDatas: {
    type: Array<TempCard>,
    required: true,
  },
});

const orangeHexagon = cdn('images/home/striped_hexagon_orange.png');
const movingCircle = cdn('images/home/circle.png');
const topRectangle = cdn('images/home/top_rectangle.png');

const containerWidth = ref(window.innerWidth);
const padLeft = ref(160);

// Initialize isDarkMode based on the root element's class
const isDarkMode = ref(false); // Initialize isDarkMode as false
let stopObserving: () => void;

function onResize() {
  if (window.innerWidth > 1024) {
    containerWidth.value = (window.innerWidth - 960) / 2 + 960;
    padLeft.value = (window.innerWidth - 1600) / 2;
  }
}

function reInitPanelHeight() {
  const panel = document.querySelector('#what-we-do-background-card');
  const card = document.querySelector('.services-card');
  if (panel && card && props.cardDatas?.length) {
    const cardHeight = card.clientHeight;
    const minHeight = 630;
    const finalHeight = Math.max(cardHeight, minHeight);
    panel.style.height = `${finalHeight}px`;
  }
}


function initAnimation() {
  if (window.innerWidth < 1025) return;

  const element1 = document.getElementById('moving-circle-image');
  const baseElement = document.getElementById('what-we-do-background-card');
  if (element1) {
    gsap.to(element1,
      {
        y: -500,
        scrollTrigger: {
          trigger: baseElement,
          // markers: true,
          scrub: true,
          start: 'top bottom',
          end: 'bottom bottom'
        }
      }
    );
  }

  const element2 = document.getElementById('orange-hexagon');
  if (element2) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {

            gsap.to(element2, {
              rotate: 45,
              scrollTrigger: {
                trigger: element2,
                // markers: true,
                scrub: true,
                start: 'top bottom',
                end: 'bottom top'
              }
            }
            );
            // Disconnect the observer after initializing the animation
            observer.disconnect();
          }
        });
      },
      { threshold: 0 } // Trigger when 50% of the element is visible
    );
    // Observe the element
    observer.observe(element2);
  }
}

// Use a MutationObserver to detect changes to the dark mode class
onMounted(() => {
  stopObserving = watchDarkMode((value: boolean) => {
    isDarkMode.value = value;
  });

  onResize();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', reInitPanelHeight);
  initAnimation();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});
</script>
